import React from "react"

// Simple, recognisable SVG icons for each hero.
// All drawn white-on-transparent — hero card provides the coloured background.

const Icon = ({ children }: { children: React.ReactNode }) => (
  <svg viewBox="0 0 40 40" fill="none" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    {children}
  </svg>
)

const W = { stroke: "white", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const }
const WF = { ...W, fill: "white", fillOpacity: 0.15 }

export const HERO_ICONS: Record<string, React.ReactNode> = {

  // ── Superman — pentagon shield + S ───────────────────────────────────────
  superman: <Icon>
    <polygon points="20,4 32,10 32,23 20,36 8,23 8,10" {...WF} />
    {/* S path */}
    <path d="M24,15 C24,13 22.5,12 20,12 C17,12 15.5,13.5 15.5,15.5 C15.5,17.5 17,18.5 20,19.5 C23,20.5 24.5,21.5 24.5,23.5 C24.5,25.5 23,27 20,27 C17,27 15.5,25.5 15.5,24" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="15.5" y1="15.5" x2="24.5" y2="15.5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    <line x1="15.5" y1="23.5" x2="24.5" y2="23.5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
  </Icon>,

  // ── Batman — bat silhouette ───────────────────────────────────────────────
  batman: <Icon>
    <path d="M20,30 C16,30 8,26 6,18 C8,15 12,16 15,18 C15,13 16,8 20,8 C24,8 25,13 25,18 C28,16 32,15 34,18 C32,26 24,30 20,30Z" fill="white" fillOpacity="0.9"/>
    <path d="M8,18 C7,14 4,12 4,12" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M32,18 C33,14 36,12 36,12" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <ellipse cx="20" cy="24" rx="3" ry="4" fill="white" fillOpacity="0.4"/>
  </Icon>,

  // ── Iron Man — arc reactor circles ───────────────────────────────────────
  "iron-man": <Icon>
    <circle cx="20" cy="20" r="15" stroke="white" strokeWidth="1.5"/>
    <circle cx="20" cy="20" r="11" stroke="white" strokeWidth="1.5"/>
    <circle cx="20" cy="20" r="7"  stroke="white" strokeWidth="1.5"/>
    <circle cx="20" cy="20" r="3"  fill="white"/>
    {/* triangle spark */}
    <path d="M20,5 L20,9 M20,31 L20,35 M5,20 L9,20 M31,20 L35,20" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
  </Icon>,

  // ── Spider-Man — web circle ───────────────────────────────────────────────
  "spider-man": <Icon>
    <circle cx="20" cy="20" r="14" stroke="white" strokeWidth="1.5"/>
    {/* web lines */}
    <line x1="20" y1="6" x2="20" y2="34" {...W}/>
    <line x1="6" y1="20" x2="34" y2="20" {...W}/>
    <line x1="10" y1="10" x2="30" y2="30" {...W}/>
    <line x1="30" y1="10" x2="10" y2="30" {...W}/>
    {/* concentric arcs */}
    <ellipse cx="20" cy="20" rx="7" ry="7" stroke="white" strokeWidth="1" fill="none"/>
    <circle cx="20" cy="20" r="2.5" fill="white"/>
  </Icon>,

  // ── Wonder Woman — star ───────────────────────────────────────────────────
  "wonder-woman": <Icon>
    <polygon points="20,4 23.5,14.5 34.5,14.5 25.5,21 28.5,32 20,26 11.5,32 14.5,21 5.5,14.5 16.5,14.5" fill="white" fillOpacity="0.9"/>
    {/* tiara arc */}
    <path d="M10,10 Q20,6 30,10" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none"/>
  </Icon>,

  // ── Black Panther — three claw marks ─────────────────────────────────────
  "black-panther": <Icon>
    {/* cat ears */}
    <path d="M10,14 L10,6 L16,12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M30,14 L30,6 L24,12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    {/* face outline */}
    <path d="M10,14 Q8,22 12,28 Q16,34 20,34 Q24,34 28,28 Q32,22 30,14" stroke="white" strokeWidth="1.5" fill="white" fillOpacity="0.1"/>
    {/* three claw lines */}
    <line x1="14" y1="18" x2="18" y2="30" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="20" y1="16" x2="20" y2="30" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="26" y1="18" x2="22" y2="30" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
  </Icon>,

  // ── Captain America — concentric circles + star ───────────────────────────
  "captain-america": <Icon>
    <circle cx="20" cy="20" r="15" stroke="white" strokeWidth="1.5" fill="white" fillOpacity="0.08"/>
    <circle cx="20" cy="20" r="10" stroke="white" strokeWidth="1.5" fill="white" fillOpacity="0.08"/>
    <circle cx="20" cy="20" r="5"  fill="white" fillOpacity="0.9"/>
    <polygon points="20,12 21.8,17.6 27.5,17.6 23,21 24.8,26.6 20,23 15.2,26.6 17,21 12.5,17.6 18.2,17.6" fill="white" fillOpacity="0.9"/>
  </Icon>,

  // ── Thor — Mjolnir hammer ─────────────────────────────────────────────────
  thor: <Icon>
    {/* hammer head */}
    <rect x="8" y="8" width="24" height="15" rx="3" fill="white" fillOpacity="0.85"/>
    {/* wrap straps */}
    <rect x="12" y="17" width="16" height="4" fill="white" fillOpacity="0.3"/>
    {/* handle */}
    <rect x="17" y="22" width="6" height="13" rx="2" fill="white" fillOpacity="0.85"/>
  </Icon>,

  // ── Hulk — raised fists ───────────────────────────────────────────────────
  hulk: <Icon>
    {/* left fist */}
    <rect x="5" y="16" width="13" height="11" rx="3" fill="white" fillOpacity="0.85"/>
    <rect x="7" y="12" width="9" height="7" rx="2" fill="white" fillOpacity="0.85"/>
    <line x1="9"  y1="19" x2="9"  y2="23" stroke="rgba(0,0,0,0.2)" strokeWidth="1"/>
    <line x1="12" y1="19" x2="12" y2="23" stroke="rgba(0,0,0,0.2)" strokeWidth="1"/>
    {/* right fist */}
    <rect x="22" y="16" width="13" height="11" rx="3" fill="white" fillOpacity="0.85"/>
    <rect x="24" y="12" width="9" height="7" rx="2" fill="white" fillOpacity="0.85"/>
    <line x1="26" y1="19" x2="26" y2="23" stroke="rgba(0,0,0,0.2)" strokeWidth="1"/>
    <line x1="29" y1="19" x2="29" y2="23" stroke="rgba(0,0,0,0.2)" strokeWidth="1"/>
  </Icon>,

  // ── Doctor Strange — Eye of Agamotto ──────────────────────────────────────
  "doctor-strange": <Icon>
    {/* outer oval */}
    <ellipse cx="20" cy="20" rx="15" ry="10" stroke="white" strokeWidth="1.5" fill="white" fillOpacity="0.08"/>
    {/* inner eye */}
    <ellipse cx="20" cy="20" rx="9" ry="6" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="1"/>
    {/* pupil — diamond */}
    <polygon points="20,15 23,20 20,25 17,20" fill="white" fillOpacity="0.9"/>
    {/* eyelashes arcs */}
    <path d="M5,20 Q8,12 14,10" stroke="white" strokeWidth="1" strokeLinecap="round" fill="none"/>
    <path d="M35,20 Q32,12 26,10" stroke="white" strokeWidth="1" strokeLinecap="round" fill="none"/>
  </Icon>,

  // ── Captain Marvel — starburst ────────────────────────────────────────────
  "captain-marvel": <Icon>
    {/* 8-point star */}
    <polygon points="20,4 22,16 34,20 22,24 20,36 18,24 6,20 18,16" fill="white" fillOpacity="0.9"/>
    <circle cx="20" cy="20" r="4" fill="white" fillOpacity="0.5"/>
  </Icon>,

  // ── Black Widow — hourglass / spider ──────────────────────────────────────
  "black-widow": <Icon>
    {/* hourglass */}
    <path d="M10,8 L30,8 L22,20 L30,32 L10,32 L18,20 Z" fill="white" fillOpacity="0.85"/>
    {/* middle band */}
    <ellipse cx="20" cy="20" rx="3" ry="2" fill="white"/>
    {/* spider legs */}
    <line x1="10" y1="14" x2="4"  y2="10" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="10" y1="26" x2="4"  y2="30" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="30" y1="14" x2="36" y2="10" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="30" y1="26" x2="36" y2="30" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
  </Icon>,

  // ── Scarlet Witch — chaos hex ─────────────────────────────────────────────
  "scarlet-witch": <Icon>
    {/* crown / W shape */}
    <path d="M6,22 L10,12 L15,20 L20,10 L25,20 L30,12 L34,22" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    {/* hex sparks */}
    <circle cx="12" cy="28" r="2" fill="white" fillOpacity="0.7"/>
    <circle cx="20" cy="30" r="2" fill="white" fillOpacity="0.7"/>
    <circle cx="28" cy="28" r="2" fill="white" fillOpacity="0.7"/>
    <path d="M8,26 L12,28 M20,30 L20,35 M32,26 L28,28" stroke="white" strokeWidth="1" strokeLinecap="round"/>
  </Icon>,

  // ── Storm — cloud + lightning ─────────────────────────────────────────────
  storm: <Icon>
    {/* cloud */}
    <path d="M8,20 Q8,14 14,14 Q15,10 20,10 Q26,10 27,14 Q32,14 32,20 Z" fill="white" fillOpacity="0.85"/>
    {/* lightning bolt */}
    <path d="M22,20 L17,28 L21,28 L19,36 L26,26 L22,26 Z" fill="white" fillOpacity="0.9"/>
  </Icon>,

  // ── Green Lantern — lantern ───────────────────────────────────────────────
  "green-lantern": <Icon>
    {/* lantern body */}
    <rect x="12" y="14" width="16" height="18" rx="4" stroke="white" strokeWidth="2" fill="white" fillOpacity="0.15"/>
    {/* top handle */}
    <path d="M17,14 L17,9 Q20,7 23,9 L23,14" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none"/>
    {/* cross bars */}
    <line x1="12" y1="20" x2="28" y2="20" stroke="white" strokeWidth="1.5"/>
    <line x1="20" y1="14" x2="20" y2="32" stroke="white" strokeWidth="1.5"/>
    {/* glow circle */}
    <circle cx="20" cy="23" r="4" fill="white" fillOpacity="0.4"/>
  </Icon>,

  // ── The Flash — lightning bolt ────────────────────────────────────────────
  "the-flash": <Icon>
    <circle cx="20" cy="20" r="15" stroke="white" strokeWidth="1.5" fill="white" fillOpacity="0.1"/>
    <path d="M23,6 L14,22 L20,22 L17,34 L28,17 L22,17 Z" fill="white" fillOpacity="0.95"/>
  </Icon>,

  // ── Aquaman — trident ─────────────────────────────────────────────────────
  aquaman: <Icon>
    {/* outer tines */}
    <line x1="10" y1="8" x2="10" y2="22" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="30" y1="8" x2="30" y2="22" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    {/* center tine (taller) */}
    <line x1="20" y1="5" x2="20" y2="22" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    {/* cross bar */}
    <line x1="10" y1="22" x2="30" y2="22" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    {/* handle */}
    <line x1="20" y1="22" x2="20" y2="36" stroke="white" strokeWidth="3" strokeLinecap="round"/>
    {/* tine tips */}
    <path d="M8,8 L10,5 L12,8" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M28,8 L30,5 L32,8" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none"/>
  </Icon>,

  // ── Hawkgirl — spread wings ───────────────────────────────────────────────
  hawkgirl: <Icon>
    {/* left wing */}
    <path d="M20,20 C16,18 10,14 4,10 C6,16 8,22 12,26 C14,24 17,22 20,20Z" fill="white" fillOpacity="0.85"/>
    {/* right wing */}
    <path d="M20,20 C24,18 30,14 36,10 C34,16 32,22 28,26 C26,24 23,22 20,20Z" fill="white" fillOpacity="0.85"/>
    {/* body */}
    <ellipse cx="20" cy="22" rx="4" ry="7" fill="white" fillOpacity="0.9"/>
    {/* helmet */}
    <path d="M16,16 Q20,10 24,16" stroke="white" strokeWidth="1.5" fill="white" fillOpacity="0.6"/>
  </Icon>,

  // ── Shuri — vibranium claw / tech ─────────────────────────────────────────
  shuri: <Icon>
    {/* panther claw beads */}
    <circle cx="20" cy="8"  r="2.5" fill="white"/>
    <circle cx="27" cy="11" r="2.5" fill="white"/>
    <circle cx="13" cy="11" r="2.5" fill="white"/>
    {/* tech triangle */}
    <polygon points="20,16 30,32 10,32" stroke="white" strokeWidth="2" fill="white" fillOpacity="0.15"/>
    {/* inner circuit */}
    <polygon points="20,20 26,30 14,30" stroke="white" strokeWidth="1" fill="white" fillOpacity="0.2"/>
    <circle cx="20" cy="26" r="2" fill="white" fillOpacity="0.8"/>
    <line x1="20" y1="16" x2="20" y2="24" stroke="white" strokeWidth="1" strokeLinecap="round"/>
  </Icon>,

  // ── Miles Morales — black/red spider ─────────────────────────────────────
  "miles-morales": <Icon>
    {/* dark spider with white accents */}
    <circle cx="20" cy="20" r="14" stroke="white" strokeWidth="1.5" fill="white" fillOpacity="0.1"/>
    {/* spider body */}
    <ellipse cx="20" cy="18" rx="4" ry="5" fill="white" fillOpacity="0.9"/>
    <ellipse cx="20" cy="26" rx="5" ry="6" fill="white" fillOpacity="0.9"/>
    {/* legs - 4 each side */}
    <path d="M16,16 L8,10 M16,18 L7,17 M16,21 L8,24 M16,24 L9,30" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M24,16 L32,10 M24,18 L33,17 M24,21 L32,24 M24,24 L31,30" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
  </Icon>,

  // ── Ms. Marvel — crescent + star ─────────────────────────────────────────
  "ms-marvel": <Icon>
    {/* crescent */}
    <path d="M28,8 Q36,20 28,32 Q20,36 12,30 Q20,28 22,20 Q20,12 12,10 Q20,4 28,8Z" fill="white" fillOpacity="0.85"/>
    {/* star */}
    <polygon points="12,18 13.5,14 15,18 11,15.5 14,15.5" fill="white" fillOpacity="0.9"/>
  </Icon>,

  // ── Wolverine — three adamantium claws ───────────────────────────────────
  wolverine: <Icon>
    {/* glove/fist */}
    <rect x="10" y="22" width="20" height="12" rx="4" fill="white" fillOpacity="0.7"/>
    {/* three claw blades */}
    <line x1="14" y1="22" x2="11" y2="5"  stroke="white" strokeWidth="3" strokeLinecap="round"/>
    <line x1="20" y1="22" x2="20" y2="4"  stroke="white" strokeWidth="3" strokeLinecap="round"/>
    <line x1="26" y1="22" x2="29" y2="5"  stroke="white" strokeWidth="3" strokeLinecap="round"/>
    {/* knuckle dots */}
    <circle cx="14" cy="24" r="1.5" fill="white" fillOpacity="0.5"/>
    <circle cx="20" cy="24" r="1.5" fill="white" fillOpacity="0.5"/>
    <circle cx="26" cy="24" r="1.5" fill="white" fillOpacity="0.5"/>
  </Icon>,

  // ── Ant-Man — helmet dome + antennae ─────────────────────────────────────
  "ant-man": <Icon>
    {/* helmet */}
    <path d="M8,24 Q8,10 20,10 Q32,10 32,24 L28,30 L12,30 Z" fill="white" fillOpacity="0.85"/>
    {/* visor */}
    <path d="M13,22 Q20,18 27,22" stroke="white" strokeWidth="2" fill="white" fillOpacity="0.3"/>
    {/* antennae */}
    <line x1="16" y1="10" x2="12" y2="4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    <line x1="24" y1="10" x2="28" y2="4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="12" cy="4" r="2" fill="white"/>
    <circle cx="28" cy="4" r="2" fill="white"/>
  </Icon>,

  // ── Wasp — wings + body ───────────────────────────────────────────────────
  wasp: <Icon>
    {/* left wings */}
    <ellipse cx="12" cy="16" rx="8" ry="5" fill="white" fillOpacity="0.6" transform="rotate(-20 12 16)"/>
    <ellipse cx="11" cy="23" rx="7" ry="4" fill="white" fillOpacity="0.4" transform="rotate(15 11 23)"/>
    {/* right wings */}
    <ellipse cx="28" cy="16" rx="8" ry="5" fill="white" fillOpacity="0.6" transform="rotate(20 28 16)"/>
    <ellipse cx="29" cy="23" rx="7" ry="4" fill="white" fillOpacity="0.4" transform="rotate(-15 29 23)"/>
    {/* body segments */}
    <ellipse cx="20" cy="14" rx="4" ry="5" fill="white" fillOpacity="0.9"/>
    <path d="M16,20 Q20,18 24,20 L23,30 Q20,33 17,30 Z" fill="white" fillOpacity="0.9"/>
    {/* stinger */}
    <line x1="20" y1="30" x2="20" y2="36" stroke="white" strokeWidth="2" strokeLinecap="round"/>
  </Icon>,

  // ── Cyborg — half human, half tech ───────────────────────────────────────
  cyborg: <Icon>
    {/* left half — human face */}
    <path d="M20,8 Q10,8 8,20 Q8,32 20,34" stroke="white" strokeWidth="2" fill="white" fillOpacity="0.15"/>
    <circle cx="14" cy="17" r="3" fill="white" fillOpacity="0.6"/>
    {/* right half — circuit face */}
    <path d="M20,8 Q30,8 32,20 Q32,32 20,34" stroke="white" strokeWidth="2" fill="white" fillOpacity="0.08"/>
    <circle cx="26" cy="17" r="3" stroke="white" strokeWidth="1.5" fill="none"/>
    <circle cx="26" cy="17" r="1.5" fill="white"/>
    {/* circuit lines */}
    <line x1="20" y1="8"  x2="20" y2="34" stroke="white" strokeWidth="1.5"/>
    <line x1="26" y1="20" x2="32" y2="20" stroke="white" strokeWidth="1"/>
    <line x1="26" y1="24" x2="30" y2="27" stroke="white" strokeWidth="1"/>
    <rect x="28" y="24" width="4" height="3" rx="1" stroke="white" strokeWidth="1" fill="none"/>
  </Icon>,

  // ── Nightwing — wing / bird shape ────────────────────────────────────────
  nightwing: <Icon>
    {/* wing spread */}
    <path d="M20,22 C16,20 8,15 4,8 C6,10 10,16 14,18 C14,14 16,10 20,10 C24,10 26,14 26,18 C30,16 34,10 36,8 C32,15 24,20 20,22Z" fill="white" fillOpacity="0.85"/>
    {/* stylised bird body */}
    <ellipse cx="20" cy="26" rx="3" ry="5" fill="white" fillOpacity="0.7"/>
    {/* mask slits */}
    <line x1="16" y1="21" x2="19" y2="22" stroke="rgba(0,0,0,0.4)" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="24" y1="21" x2="21" y2="22" stroke="rgba(0,0,0,0.4)" strokeWidth="1.5" strokeLinecap="round"/>
  </Icon>,

  // ── Rogue — power absorption streak ──────────────────────────────────────
  rogue: <Icon>
    {/* face silhouette */}
    <ellipse cx="20" cy="20" rx="12" ry="14" stroke="white" strokeWidth="1.5" fill="white" fillOpacity="0.1"/>
    {/* white streak through hair */}
    <path d="M14,6 Q16,10 14,16 Q13,20 15,26" stroke="white" strokeWidth="5" strokeLinecap="round" fill="none"/>
    <path d="M14,6 Q16,10 14,16 Q13,20 15,26" stroke="rgba(255,255,255,0.9)" strokeWidth="3" strokeLinecap="round" fill="none"/>
    {/* power glow hands */}
    <circle cx="10" cy="32" r="4" stroke="white" strokeWidth="1.5" fill="white" fillOpacity="0.3"/>
    <circle cx="30" cy="32" r="4" stroke="white" strokeWidth="1.5" fill="white" fillOpacity="0.3"/>
  </Icon>,

  // ── Zatanna — top hat + wand ──────────────────────────────────────────────
  zatanna: <Icon>
    {/* top hat */}
    <rect x="13" y="8" width="14" height="16" rx="2" fill="white" fillOpacity="0.85"/>
    {/* brim */}
    <rect x="8" y="22" width="24" height="4" rx="2" fill="white" fillOpacity="0.85"/>
    {/* wand */}
    <line x1="24" y1="28" x2="34" y2="38" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="34" cy="38" r="2.5" fill="white"/>
    {/* star sparkles from hat */}
    <circle cx="20" cy="14" r="2" fill="white" fillOpacity="0.5"/>
    <line x1="14" y1="10" x2="12" y2="8"  stroke="white" strokeWidth="1" strokeLinecap="round"/>
    <line x1="26" y1="10" x2="28" y2="8"  stroke="white" strokeWidth="1" strokeLinecap="round"/>
    <line x1="20" y1="8"  x2="20" y2="6"  stroke="white" strokeWidth="1" strokeLinecap="round"/>
  </Icon>,
}
